import React from 'react';
import { FileText, Upload, CreditCard, CheckCircle, Clock, Shield, Mail, Hash, ArrowRight, Info, AlertCircle } from 'lucide-react';

const Step = ({ number, title, description, icon: Icon, color, children }) => (
  <div className="flex gap-4 items-start" data-testid={`guide-step-${number}`}>
    <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: color }}>
      {number}
    </div>
    <div className="flex-1 pb-8 border-l-2 border-slate-100 pl-6 -ml-5 last:border-0">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={16} style={{ color }} />}
        <h3 className="text-base font-bold text-slate-900">{title}</h3>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed">{description}</p>
      {children}
    </div>
  </div>
);

const DocItem = ({ label, required }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-slate-200">
    <Upload size={14} className="text-slate-400" />
    <span className="text-sm text-slate-700">{label}</span>
    {required && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 py-0.5 rounded font-medium">Obbligatorio</span>}
  </div>
);

export const GuidaCollaboratore = () => {
  return (
    <div className="p-3 sm:p-6 md:p-8 max-w-3xl mx-auto" data-testid="guida-page">
      {/* Header */}
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-700 text-xs font-medium mb-4">
          <Info size={12} /> Guida operativa
        </div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Come inserire una Richiesta di Emissione</h1>
        <p className="text-sm text-slate-500 mt-2">Segui questi passaggi per inviare correttamente una richiesta di emissione polizza all'agenzia.</p>
        <a href="/guida-collaboratore.pdf" download className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 transition-colors" data-testid="download-guida-pdf">
          <FileText size={14} /> Scarica PDF
        </a>
      </div>

      {/* Steps */}
      <div className="space-y-2">
        <Step number={1} title="Clicca su Nuova Richiesta" icon={FileText} color="#3B82F6"
          description="Dalla pagina 'Richieste Emissione', clicca il pulsante '+ Nuova Richiesta' in alto a destra. Si aprirà il modulo di inserimento."
        />

        <Step number={2} title="Compila i dati del cliente" icon={Shield} color="#6366F1"
          description="Inserisci i dati richiesti:">
          <div className="mt-3 space-y-2 bg-slate-50 rounded-xl p-4">
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div><span className="font-medium text-slate-700">Nome e Cognome</span> <span className="text-red-400">*</span></div>
              <div className="text-slate-500">Del contraente</div>
              <div><span className="font-medium text-slate-700">Compagnia</span> <span className="text-red-400">*</span></div>
              <div className="text-slate-500">Es. Allianz, UnipolSai, Nobis...</div>
              <div><span className="font-medium text-slate-700">Tipo Polizza</span> <span className="text-red-400">*</span></div>
              <div className="text-slate-500">Auto, Moto, Casa, Vita, ecc.</div>
              <div><span className="font-medium text-slate-700">Premio</span></div>
              <div className="text-slate-500">Importo + Annuale o Semestrale</div>
              <div><span className="font-medium text-slate-700">Targa</span></div>
              <div className="text-slate-500">Se polizza veicolo</div>
              <div><span className="font-medium text-slate-700">Note</span></div>
              <div className="text-slate-500">Informazioni aggiuntive</div>
            </div>
          </div>
        </Step>

        <Step number={3} title="Carica i documenti" icon={Upload} color="#F59E0B"
          description="Carica i documenti necessari cliccando 'Carica' accanto ad ogni voce. Puoi caricare PDF, immagini (JPG, PNG) o documenti Word.">
          <div className="mt-3 space-y-2">
            <DocItem label="Libretto da assicurare" required />
            <DocItem label="Documento d'identità" />
            <DocItem label="Tessera sanitaria" />
            <DocItem label="Patente" />
          </div>
          <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <p className="text-xs font-semibold text-amber-800 flex items-center gap-1.5 mb-1"><AlertCircle size={12} /> Caso Bersani</p>
            <p className="text-xs text-amber-700">Se si tratta di <strong>Bersani</strong>, seleziona "Bersani stesso" o "Bersani diverso". In caso di Bersani diverso, carica anche lo <strong>Stato di famiglia</strong> e il <strong>Libretto targa Bersani</strong>.</p>
          </div>
        </Step>

        <Step number={4} title="Allega il Preventivo" icon={Hash} color="#8B5CF6"
          description="Puoi allegare il file del preventivo (da Preventivass o dal portale della compagnia) oppure inserire semplicemente il numero di preventivo nel campo dedicato. Se hai entrambi, inserisci sia il numero che il file."
        />

        <Step number={5} title="Invia la Richiesta" icon={CheckCircle} color="#10B981"
          description="Clicca 'Invia Richiesta' in fondo al modulo. La richiesta verrà inviata all'agenzia e riceverai aggiornamenti via email sullo stato di avanzamento."
        />
      </div>

      {/* Workflow */}
      <div className="mt-10 mb-8">
        <h2 className="text-xl font-bold text-slate-900 mb-4">Come funziona il processo</h2>
        <p className="text-sm text-slate-500 mb-4">Dopo l'invio, la tua richiesta seguirà questo percorso:</p>
        <div className="space-y-3">
          {[
            { stato: 'In Attesa', desc: "La richiesta è stata ricevuta dall'agenzia", color: '#F59E0B', icon: Clock },
            { stato: 'Presa in Carico', desc: "L'agenzia sta verificando la documentazione", color: '#3B82F6', icon: Shield },
            { stato: 'Richiesta Pagamento', desc: 'Ti verrà chiesto di allegare il pagamento', color: '#A855F7', icon: CreditCard },
            { stato: 'Pagamento Accettato', desc: "L'agenzia ha confermato il pagamento", color: '#8B5CF6', icon: CheckCircle },
            { stato: 'Incasso Inviato', desc: "L'agenzia ha inviato la richiesta alla compagnia", color: '#F97316', icon: Mail },
            { stato: 'Emessa', desc: 'La polizza è stata emessa! Troverai il documento allegato.', color: '#10B981', icon: CheckCircle },
          ].map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: s.color + '15' }}>
                <s.icon size={14} style={{ color: s.color }} />
              </div>
              <div className="flex-1">
                <span className="text-sm font-semibold" style={{ color: s.color }}>{s.stato}</span>
                <span className="text-sm text-slate-500 ml-2">{s.desc}</span>
              </div>
              {i < 5 && <ArrowRight size={14} className="text-slate-300 flex-shrink-0" />}
            </div>
          ))}
        </div>
      </div>

      {/* Communications */}
      <div className="bg-blue-50 rounded-xl p-5 border border-blue-100">
        <h3 className="text-sm font-bold text-blue-900 flex items-center gap-2 mb-2"><Mail size={16} /> Comunicazioni</h3>
        <p className="text-sm text-blue-800 leading-relaxed">
          Nella pagina di dettaglio di ogni richiesta troverai una sezione <strong>"Comunicazioni"</strong> dove puoi scambiare messaggi con l'agenzia. 
          Ogni messaggio che scrivi viene automaticamente inviato via email all'agenzia, e viceversa.
          Usa questa sezione per chiedere informazioni, inviare documenti aggiuntivi o ricevere aggiornamenti.
        </p>
      </div>

      {/* Tips */}
      <div className="mt-6 bg-slate-50 rounded-xl p-5 border border-slate-200">
        <h3 className="text-sm font-bold text-slate-900 mb-3">Consigli utili</h3>
        <ul className="space-y-2 text-sm text-slate-600">
          <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Assicurati che i documenti siano leggibili e completi prima di caricarli</li>
          <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Il libretto da assicurare è obbligatorio per procedere</li>
          <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Quando ricevi la richiesta di pagamento, allega la ricevuta il prima possibile</li>
          <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Controlla la tua email per gli aggiornamenti sullo stato della pratica</li>
          <li className="flex items-start gap-2"><CheckCircle size={14} className="text-green-500 mt-0.5 flex-shrink-0" /> Usa la sezione "Comunicazioni" per qualsiasi domanda o chiarimento</li>
        </ul>
      </div>
    </div>
  );
};

export default GuidaCollaboratore;
